import { useState } from 'react';

export const FormState = (form: any, submitFunction: any) => {
  const [ready, setReady] = useState(false);
  const inputValidator = (field: string, validation: any, value: string) => {
    const { required, pattern, min, max, notEmpty } = validation;
    if (required && typeof value === 'boolean' && value === null) return 'ERROR_' + field.toUpperCase() + '_REQUIRED';
    if (required && typeof value !== 'boolean' && !value) return 'ERROR_' + field.toUpperCase() + '_REQUIRED';

    if (required && Array.isArray(value) && !value.length) {
      return 'ERROR_' + field.toUpperCase() + '_REQUIRED';
    }

    if (min && value.length < min) return 'ERROR_' + field.toUpperCase() + '_MIN';
    if (max && value.length > max) return 'ERROR_' + field.toUpperCase() + '_MAX';
    if (notEmpty && value.length === 0) return 'ERROR_FIELD_NOT_EMPTY';

    if (pattern) {
      if (required) {
        const regex = new RegExp(pattern);
        return regex.test(value) ? '' : 'ERROR_' + field.toUpperCase() + '_PATTERN';
      } else {
        if (value.length > 0) {
          const regex = new RegExp(pattern);
          return regex.test(value) ? '' : 'ERROR_' + field.toUpperCase() + '_PATTERN';
        }
      }
    }

    return '';
  };

  const passwordConfirmationValidator = (password_confirm: string, password: string) => {
    if (password_confirm !== password) {
      return 'ERROR_' + 'password_confirm'.toUpperCase() + '_CONFIRM';
    }
    return '';
  };

  const initForm = (form: any) => {
    let formValues = {};
    let inputValidation = {};
    for (const key in form) {
      formValues = { ...formValues, [key]: form[key].value };
      inputValidation = { ...inputValidation, [key]: form[key].validate };
    }

    const inputValues = { ...formValues };
    let inputHasErrors = {};
    let inputVisited = {};

    for (const key in formValues) {
      inputHasErrors = {
        ...inputHasErrors,
        // @ts-ignore
        [key]: form[key].value ? inputValidator(key, inputValidation[key], formValues[key]) : '',
      };

      // @ts-ignore
      inputVisited = { ...inputHasErrors, [key]: toString(formValues[key]) ? true : false };
    }

    return {
      inputValues: { ...inputValues },
      inputHasErrors: { ...inputHasErrors },
      inputVisited: { ...inputVisited },
      formIsValid: false,
      inputValidation: { ...inputValidation },
    };
  };

  // formState is the data that we need
  const [formState, setFormState] = useState(initForm(form));

  // inputChangeHandler activate when we type on input
  const inputChangeHandler = (inputIdentifier: any, value: any, hasError: any, visited: any) => {
    const updatedValues = {
      ...formState.inputValues,
      [inputIdentifier]: value,
    };

    const inputValidation = { ...formState.inputValidation };
    if (visited) {
      // @ts-ignore
      hasError = inputValidator(inputIdentifier, inputValidation[inputIdentifier], value);
      if (inputIdentifier === 'conditions' && !value) hasError = 'ERROR_CONDITIONS_REQUIRED';
      else if (inputIdentifier === 'password_confirm' && !hasError) {
        // @ts-ignore
        hasError = passwordConfirmationValidator(value, formState.inputValues['password']);
      }
    }

    const updateHasErrors = {
      ...formState.inputHasErrors,
      [inputIdentifier]: hasError,
    };

    const updateVisited = {
      ...formState.inputVisited,
      [inputIdentifier]: visited,
    };

    let updatedFormIsValid = true;
    for (const key in updateHasErrors) {
      // @ts-ignore
      updatedFormIsValid = updatedFormIsValid && !updateHasErrors[key];
    }

    setFormState({
      inputValidation: inputValidation,
      inputValues: updatedValues,
      inputHasErrors: updateHasErrors,
      inputVisited: updateVisited,
      formIsValid: updatedFormIsValid,
    });
  };

  // inputChangeHandler activate when we exit the input to validate the data inserted
  const inputBlurHandler = (inputIdentifier: any) => {
    // @ts-ignore
    const value = formState.inputValues[inputIdentifier];
    // @ts-ignore
    const hasError = formState.inputHasErrors[inputIdentifier];
    inputChangeHandler(inputIdentifier, value, hasError, true);
  };

  // Validate all fields synchronously. Returns true when the form can be submitted.
  const formVerifyHandler = () => {
    const values = { ...formState.inputValues };
    const inputValidation = { ...formState.inputValidation };
    let errors = {},
      errorText;

    for (const key in values) {
      // @ts-ignore
      errorText = inputValidator(key, inputValidation[key], values[key]);
      if (key === 'password_confirm' && !errorText) {
        // @ts-ignore
        errorText = passwordConfirmationValidator(values[key], values.password);
      }
      if (errorText) errors = { ...errors, [key]: errorText };
    }

    const isValid = Object.keys(errors).length === 0;
    setFormState({
      ...formState,
      inputHasErrors: errors,
      formIsValid: isValid,
    });
    return isValid;
  };

  // submit form — validate in the same turn so we never race with async setState
  // (previous isSubmitting + useEffect pattern dropped valid submits with no UI feedback)
  const submitHandler = (e: any) => {
    if (e) e.preventDefault();
    if (formVerifyHandler()) {
      submitFunction();
    }
  };

  const inputErrorHandler = (errors: any) => {
    setFormState((prevState) => {
      return { ...prevState, ...errors };
    });
  };

  const resetFormState = () => {
    let updatedValues = {};
    let updateHasErrors = {};
    let updateVisited = {};

    for (const key in form) {
      updatedValues = { ...updatedValues, [key]: '' };
      updateHasErrors = { ...updateHasErrors, [key]: '' };
      updateVisited = { ...updateVisited, [key]: false };
    }

    setFormState({
      ...formState,
      inputValues: updatedValues,
      inputHasErrors: updateHasErrors,
      inputVisited: updateVisited,
      formIsValid: false,
    });
  };

  const updateFormState = (dataObject: any) => {
    setReady(true);
    setFormState((prevState) => {
      return {
        ...prevState,
        inputValues: dataObject,
      };
    });
  };

  const handleErrorsServer = (enteredErrors: string[] | string) => {
    const list = Array.isArray(enteredErrors) ? enteredErrors : enteredErrors ? [enteredErrors] : [];
    let newErrors = {};
    for (const fieldName in formState.inputValues) {
      for (const enteredError of list) {
        if (typeof enteredError === 'string' && enteredError.includes(fieldName.toUpperCase())) {
          newErrors = { ...newErrors, [fieldName]: enteredError };
          break;
        }
      }
    }

    setFormState((prevState) => {
      return {
        ...prevState,
        inputHasErrors: newErrors,
      };
    });
  };

  const field = (fieldName: string, typeInput = 'text', defaultValue = '') => {
    if (typeInput === 'file') {
      return {
        name: fieldName,
        // @ts-ignore
        inputHasErrors: formState.inputHasErrors[fieldName],
        // @ts-ignore
        onChange: (e: any) => {
          // @ts-ignore
          inputChangeHandler(
            fieldName,
            e.target.files[0],
            // @ts-ignore
            formState.inputHasErrors[fieldName],
            // @ts-ignore
            formState.inputVisited[fieldName]
          );
        },
        onBlur: () => {
          inputBlurHandler(fieldName);
        },
        ready: ready,
        // @ts-ignore
        defaultValue: formState.inputValues[fieldName],
      };
    } else if (typeInput === 'autoComplete') {
      return {
        name: fieldName,
        // @ts-ignore
        inputHasErrors: formState.inputHasErrors[fieldName],
        onChange: (e: any, v: any) => {
          // @ts-ignore
          inputChangeHandler(
            fieldName,
            v ? v.value : ' ',
            // @ts-ignore
            formState.inputHasErrors[fieldName],
            // @ts-ignore
            formState.inputVisited[fieldName]
          );
        },
        // @ts-ignore
        defaultValue: formState.inputValues[fieldName],
        onBlur: () => {
          inputBlurHandler(fieldName);
        },
        multiple: false,
        ready: ready,
      };
    } else if (typeInput === 'multipleAutoComplete') {
      return {
        name: fieldName,
        // @ts-ignore
        inputHasErrors: formState.inputHasErrors[fieldName],
        onChange: (e: any, v: any) => {
          // @ts-ignore
          inputChangeHandler(
            fieldName,
            v.map((value: any) => value.value),
            // @ts-ignore
            formState.inputHasErrors[fieldName],
            // @ts-ignore
            formState.inputVisited[fieldName]
          );
        },
        onBlur: () => {
          inputBlurHandler(fieldName);
        },
        // @ts-ignore
        defaultValue:
          // @ts-ignore
          formState.inputValues[fieldName] && Array.isArray(formState.inputValues[fieldName])
            ? // @ts-ignore
              [...formState.inputValues[fieldName]]
            : null,
        multiple: true,
        ready: ready,
      };
    } else if (typeInput === 'checkbox') {
      return {
        name: fieldName,
        // @ts-ignore
        inputHasErrors: formState.inputHasErrors[fieldName],
        type: 'checkbox',
        // @ts-ignore
        checked: formState.inputValues[fieldName],
        defaultChecked: false,
        onClick: () => {
          // @ts-ignore
          inputChangeHandler(
            fieldName,
            // @ts-ignore
            !formState.inputValues[fieldName],
            // @ts-ignore
            formState.inputHasErrors[fieldName],
            // @ts-ignore
            formState.inputVisited[fieldName]
          );
        },
      };
    } else if (typeInput === 'radio') {
      return {
        name: fieldName,
        type: 'radio',
        // @ts-ignore
        checked: defaultValue === formState.inputValues[fieldName],
        onChange: (e: any) => {
          // @ts-ignore
          inputChangeHandler(fieldName, e.target.value, formState.inputHasErrors[fieldName], formState.inputVisited[fieldName]);
        },
        value: defaultValue,
        // @ts-ignore
        error: formState.inputHasErrors[fieldName],
      };
    } else {
      return {
        name: fieldName,
        // @ts-ignore
        inputHasErrors: formState.inputHasErrors[fieldName],
        // @ts-ignore
        value: formState.inputValues[fieldName],
        onChange: (e: any) => {
          // @ts-ignore
          inputChangeHandler(fieldName, e.target.value, formState.inputHasErrors[fieldName], formState.inputVisited[fieldName]);
        },
        onBlur: () => {
          inputBlurHandler(fieldName);
        },
      };
    }
  };

  return {
    formState,
    inputChangeHandler,
    inputBlurHandler,
    inputErrorHandler,
    submitHandler,
    resetFormState,
    updateFormState,
    handleErrorsServer,
    field,
  };
};
